import { FieldValues, UseFormReturn } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { FormError, Label } from 'shared/atoms'
import { useFetchPayoutMethods, usePayoutMethodLabels } from 'shared/hooks'
import { InputGroup } from 'shared/molecules'
import { BankAccountPartsInput } from '../BankAccountPartsInput/BankAccountPartsInput'

type PayoutDetailsFieldsProps = {
	form: UseFormReturn<FieldValues>
}

const BIZUM_PLACEHOLDER = '+34 612 345 678'

export const PayoutDetailsFields = ({ form }: PayoutDetailsFieldsProps) => {
	const { t } = useTranslation()
	const { payoutMethods, bankAccountPlaceholder, country } = useFetchPayoutMethods()
	const payoutMethodLabels = usePayoutMethodLabels()
	const {
		register,
		watch,
		formState: { errors }
	} = form
	const fieldErrors = errors as any
	const selectedMethod = watch('payout_method')

	const offersBank = payoutMethods.some((method) => method.kind === 'bank')
	const offersBizum = payoutMethods.some((method) => method.kind === 'bizum')
	const hasChoice = payoutMethods.length > 0
	const hasParts = !!bankAccountPlaceholder?.includes('-')

	return (
		<>
			{offersBank && hasParts && (
				<BankAccountPartsInput
					form={form}
					placeholder={bankAccountPlaceholder as string}
					country={country}
					error={fieldErrors.bank_account}
				/>
			)}
			{offersBank && !hasParts && (
				<InputGroup
					id="bank_account"
					className="sm:col-span-2"
					label={t('Bank account')}
					placeholder={bankAccountPlaceholder ?? undefined}
					autoComplete="off"
					{...register('bank_account')}
					error={fieldErrors.bank_account}
				/>
			)}
			{offersBizum && (
				<InputGroup
					id="bizum_phone"
					type="tel"
					className="sm:col-span-2"
					label={t('Bizum')}
					placeholder={BIZUM_PLACEHOLDER}
					autoComplete="off"
					{...register('bizum_phone')}
					error={fieldErrors.bizum_phone}
				/>
			)}
			{hasChoice && (
				<div className="flex flex-col gap-y-1.5 sm:col-span-2">
					<Label text={t('Pay to')} />
					<div className="grid grid-cols-2 gap-2">
						{payoutMethods.map((method) => (
							<label
								key={method.kind}
								className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border p-2.5 transition-colors hover:bg-muted/50 [&:has(:checked)]:border-primary [&:has(:checked)]:bg-primary-soft">
								<input
									type="radio"
									className="h-4 w-4 shrink-0 accent-primary"
									value={method.kind}
									{...register('payout_method')}
								/>
								<span className="text-[13px] font-medium normal-case text-foreground">
									{payoutMethodLabels[method.kind]}
								</span>
							</label>
						))}
					</div>
					{!selectedMethod && !fieldErrors.payout_method && (
						<span className="text-[11px] text-muted-foreground">
							{t('Nothing selected yet. Choose where your payouts should go.')}
						</span>
					)}
					<FormError text={fieldErrors.payout_method?.message} />
				</div>
			)}
		</>
	)
}
